import Vue from 'vue';
import axios from "axios";
import eventsOddsHandler from "@/services/eventsOddsHandler";
import assign from 'lodash/assign';
import {detectGoalPosition} from '@/services/goalDetector';
import {reapplyKeyOnMarkets} from '@/services/keyLiveReapply.service';
import {sync as syncFrozen, clearAll as clearFrozenRegistry} from '@/services/frozenOddsRegistry.service';

const DEFAULT_PAGINATION_LIMIT = 100;

// version 3.2 Exact Timestamp Monotonic Livescore Fix

// --- CONSTANTS ---
const EVENT_STATUS = {
    PREMATCH: 1,
    LIVE: 2,
    FINISHED: 3,
    STARTING_SOON: 9
};

// --- LOCAL LOOKUP TABLES ---
let eventsLookupTable = {};
let oddsLookupTable = {};

// --- GOAL BADGE TIMERS ---
const _goalTimers = new Map();

let _typeChangeResetTimer = null;
const TYPE_CHANGE_RESET_DELAY = 2500;

function _scheduleTypeChangeReset(commit) {
    if (_typeChangeResetTimer) clearTimeout(_typeChangeResetTimer);
    _typeChangeResetTimer = setTimeout(() => {
        commit('RESET_TYPE_CHANGES');
        _typeChangeResetTimer = null;
    }, TYPE_CHANGE_RESET_DELAY);
}

const finishedEventIds = new Set();

// --- HELPER: MONOTONIC UPDATE ---
// Estrae il timestamp in millisecondi in modo sicuro sia dal root che dall'oggetto livescore
function extractLastUpdate(payload) {
    if (!payload) return 0;
    // 1. lastUpdate at root (es. dal nuovo formato socket o dal polling root)
    if (payload.lastUpdate) return new Date(payload.lastUpdate).getTime();

    // 2. lastUpdate inside livescore object or array
    if (payload.livescore) {
        const ls = Array.isArray(payload.livescore) ? payload.livescore[0] : payload.livescore;
        if (ls && ls.lastUpdate) {
            return new Date(ls.lastUpdate).getTime();
        }
    }
    return 0;
}

function isPayloadStale(existing, incoming) {
    if (!existing || !incoming) return false;

    // 1. STATUS MONOTONICITY (Protezione stato evento)
    const exStatus = parseInt(existing.status || existing.livescore?.scoreboard?.status || '0');

    let inStatus = parseInt(incoming.status || '0');
    if (inStatus === 0 && incoming.livescore) {
        const inLs = Array.isArray(incoming.livescore) ? incoming.livescore[0] : incoming.livescore;
        inStatus = parseInt(inLs?.scoreboard?.status || '0');
    }

    // Regola 1: Non si torna a Prematch/StartingSoon se siamo Live o Finiti
    if ((inStatus === EVENT_STATUS.PREMATCH || inStatus === EVENT_STATUS.STARTING_SOON) &&
        (exStatus === EVENT_STATUS.LIVE || exStatus === EVENT_STATUS.FINISHED)) {
        return true;
    }
    // Regola 2: Non si torna a Live se siamo Finiti
    if (inStatus === EVENT_STATUS.LIVE && exStatus === EVENT_STATUS.FINISHED) {
        return true;
    }

    // 2. EXACT TIMESTAMP MONOTONICITY
    const exTime = extractLastUpdate(existing);
    const inTime = extractLastUpdate(incoming);

    if (exTime > 0 && inTime > 0 && inTime < exTime) {
        // Il payload in arrivo è certificatamente più vecchio di quello a schermo. Scartato.
        // console.log(`[Monotonic Shield] Scartato payload obsoleto. A schermo: ${exTime}, In arrivo: ${inTime}`);
        return true;
    }

    return false;
}

function _resolveLimit(rootGetters, ishome) {
    const val = rootGetters['settings/getLivePreviewMaxEvents'];
    const valHome = rootGetters['settings/getLiveNowPreviewMaxEventsForPage'];
    if (ishome) return (typeof valHome === 'number' && !isNaN(valHome)) ? valHome : DEFAULT_PAGINATION_LIMIT;
    return (typeof val === 'number' && !isNaN(val)) ? val : DEFAULT_PAGINATION_LIMIT;
}

function mapSingleMarketToLookup(market, eventId) {
    if (!market || !market.outcome || !Array.isArray(market.outcome)) return;
    for (let k = 0; k < market.outcome.length; k++) {
        const odd = market.outcome[k];
        if (odd.unique_id) {
            oddsLookupTable[odd.unique_id] = {odd, market, eventId: eventId || null};
            // Keep the shared frozen-odds registry in sync so the cart
            // (and any other cross-store consumer) can query "is this uid
            // currently frozen?" in O(1) without touching module scope.
            syncFrozen(odd.unique_id, odd.frozenApplied === true);
        }
    }
}

async function _fetchLivePreviewPage(apiUrl, token, lang, page, limit, sport, platformId, tournamentId) {
    const body = {sportId: sport || undefined, lang, page, limit, platformId};
    if (tournamentId != null) body.tournamentId = tournamentId;
    const res = await axios.post(
        `${apiUrl}/v3/events/live/sports`,
        body,
        {headers: {Authorization: `Bearer ${token}`}}
    );

    const result = res.data?.result;
    if (!result?.groupedEvents) return null;

    const freshData = await eventsOddsHandler.refactorLivePreviewV3(
        result.groupedEvents,
        result.headerSign,
        result.sportsAvailable
    );

    freshData.headerSignOvertime = result.headerSignOvertime || null;
    freshData.headerSignPenalty = result.headerSignPenalty || null;

    return {
        freshData,
        sportsAvailable: result.sportsAvailable,
        categoriesAvailable: result.categoriesAvailable,
        headerSignOvertime: result.headerSignOvertime || null,
        headerSignPenalty: result.headerSignPenalty || null,
        sportId: result.sportId,
        pagination: res.data.pagination || null
    };
}

// --- HELPER FUNCTIONS FOR SILENT PATCH ---
function _patchEventLivescore(existing, fresh) {
    if (!fresh.livescore) return;
    if (!existing.livescore) {
        Vue.set(existing, 'livescore', fresh.livescore);
        return;
    }
    if (fresh.livescore.scoreboard) {
        const freshSb = fresh.livescore.scoreboard;
        const existSb = existing.livescore.scoreboard || {};
        const merged = assign({}, existSb, freshSb);

        Vue.set(existing.livescore, 'scoreboard', merged);
    }
    if (fresh.livescore.periods) Vue.set(existing.livescore, 'periods', fresh.livescore.periods);
    if (fresh.livescore.matchStatus) Vue.set(existing.livescore, 'matchStatus', fresh.livescore.matchStatus);
}

function _patchEventOdds(existing, freshOdds, eventId) {
    if (!freshOdds || !Array.isArray(freshOdds)) return;

    for (let k = 0; k < freshOdds.length; k++) {
        const market = freshOdds[k];
        if (!market || !Array.isArray(market.outcome)) continue;

        for (let m = 0; m < market.outcome.length; m++) {
            const outcome = market.outcome[m];
            let targetOdd = null;
            let targetMarket = null;

            if (outcome.unique_id) {
                const ref = oddsLookupTable[outcome.unique_id];
                if (ref && (ref.eventId == null || ref.eventId === eventId)) {
                    targetOdd = ref.odd;
                    targetMarket = ref.market;
                }
            }

            if (!targetOdd && Array.isArray(existing.odds)) {
                const freshMktKey = market.unique_id || String(market.marketId || market.id || '');
                for (let mk = 0; mk < existing.odds.length; mk++) {
                    const exMkt = existing.odds[mk];
                    if (!exMkt || !Array.isArray(exMkt.outcome)) continue;

                    const exMktKey = exMkt.unique_id || String(exMkt.marketId || exMkt.id || '');
                    if (freshMktKey && exMktKey && freshMktKey !== exMktKey) continue;

                    for (let oc = 0; oc < exMkt.outcome.length; oc++) {
                        const exOdd = exMkt.outcome[oc];
                        const sameId = (
                            (outcome.outcome_id != null && String(exOdd.outcome_id) === String(outcome.outcome_id)) ||
                            (outcome.id_outcome != null && String(exOdd.id_outcome) === String(outcome.id_outcome)) ||
                            (outcome.ext_id != null && String(exOdd.ext_id) === String(outcome.ext_id)) ||
                            (outcome.outcomeId != null && String(exOdd.outcomeId) === String(outcome.outcomeId))
                        );
                        const sameName = !sameId && (exOdd.name || exOdd.outcomeName) === (outcome.name || outcome.outcomeName);

                        if (sameId || sameName) {
                            targetOdd = exOdd;
                            targetMarket = exMkt;
                            if (outcome.unique_id) {
                                oddsLookupTable[outcome.unique_id] = {odd: exOdd, market: exMkt, eventId};
                            }
                            break;
                        }
                    }
                    if (targetOdd) break;
                }
            }

            if (!targetOdd) continue;

            const newVal = parseFloat(outcome.outcome_odd_value);
            const oldVal = parseFloat(targetOdd.outcome_odd_value);

            if (newVal > oldVal) Vue.set(targetOdd, 'type_change', 'up');
            else if (newVal < oldVal) Vue.set(targetOdd, 'type_change', 'down');

            targetOdd.outcome_odd_value = newVal;
            targetOdd.status = outcome.status;
            targetOdd.active = outcome.active;
            if (outcome.mainLine != null) targetOdd.mainLine = outcome.mainLine;
            if (targetMarket && market.specifier != null) targetMarket.specifier = market.specifier;

            // KEY re-apply sync — mirror the server-side rule change onto the
            // local market so the next socket ODDS_CHANGE_LIVE rescales using
            // the fresh keyApplied value instead of the stale one loaded at
            // page open. Without this, an admin edit of a KEY rule while the
            // player has the screen open would surface the correct value on
            // the silent refresh (server ships it in `outcome_odd_value`) but
            // the very next socket update would reapply the old cached
            // keyApplied and drift the odds. Also clears `keyFallback` when
            // the server no longer reports one (rule normalized).
            if (targetMarket) {
                if (market.keyApplied != null) {
                    if (targetMarket.keyApplied !== market.keyApplied) {
                        Vue.set(targetMarket, 'keyApplied', market.keyApplied);
                    }
                } else if (targetMarket.keyApplied != null) {
                    // Rule was DELETED admin-side → server stops emitting keyApplied.
                    Vue.set(targetMarket, 'keyApplied', null);
                }
                if (market.keyFallback != null) {
                    if (targetMarket.keyFallback !== market.keyFallback) {
                        Vue.set(targetMarket, 'keyFallback', market.keyFallback);
                    }
                } else if (targetMarket.keyFallback != null) {
                    Vue.set(targetMarket, 'keyFallback', null);
                }
            }

            // Freeze markers — mirror `SILENT_PATCH_LIVE_DETAIL` in detailLive.
            // Without this, a freeze rule created after page load would
            // suspend the odd on silent refresh but the very next socket
            // ODDS_CHANGE_LIVE would reopen it (guard reads `frozenApplied`,
            // which stayed undefined locally). See detailLive.js for the
            // full write-up.
            if (outcome.frozenApplied === true) {
                Vue.set(targetOdd, 'frozenApplied', true);
            } else if (targetOdd.frozenApplied === true) {
                Vue.set(targetOdd, 'frozenApplied', false);
            }
            // FRZ.2 — keep the shared registry in lockstep with the local
            // Vue state so cart.socketClearSuspendedOnRecover reads the
            // right answer for this uid on the very next socket update.
            syncFrozen(targetOdd.unique_id, targetOdd.frozenApplied === true);

            // BOOST silent-refresh sync — mirror the KEY/FREEZE pattern.
            // An admin edit of a boost rule while the player has the screen
            // open must update the badge on the next silent refresh, not
            // stay stale until page reload. Symmetric case: rule DELETED
            // → payload no longer carries boostApplied → clear the local
            // flag so the badge disappears immediately.
            if (outcome.boostApplied === true) {
                Vue.set(targetOdd, 'boostApplied', true);
                if (outcome.boostPercent != null) {
                    Vue.set(targetOdd, 'boostPercent', Number(outcome.boostPercent));
                }
            } else if (targetOdd.boostApplied === true) {
                Vue.set(targetOdd, 'boostApplied', false);
                Vue.set(targetOdd, 'boostPercent', null);
            }
        }
    }
}

// --- VUEX MODULE ---
const state = {
    previewLive: {events: [], headerSign: [], headerSignOvertime: null, headerSignPenalty: null},
    liveEventsPrimaryMarket: [],
    countLive: [],
    liveSportSelected: {},
    activeSport: null,
    previewLimit: null,
    loading: false,
    loadingMore: false,
    pagination: {
        page: 1,
        limit: DEFAULT_PAGINATION_LIMIT,
        total: 0,
        totalPages: 0
    },
    // Client-side goal badge state: { [eventId]: position }
    // Controlled exclusively by timers — immune to server data overwrites.
    activeGoalEvents: {},
};

const getters = {
    getPreviewLive: (state) => state.previewLive,
    getCountLive: (state) => state.countLive,
    getLiveSportSelected: (state) => state.liveSportSelected,
    isLoadingLive: (state) => state.loading,
    isLoadingMore: (state) => state.loadingMore,
    getPagination: (state) => state.pagination,
    getEventPrimaryMarket: (state) => state.liveEventsPrimaryMarket,
    getActiveGoalEvents: (state) => state.activeGoalEvents
};

const mutations = {
    SET_LOADING_LIVE(state, payload) {
        state.loading = (payload && typeof payload === 'object' && 'status' in payload) ? !!payload.status : !!payload;
    },
    SET_LOADING_MORE(state, status) {
        state.loadingMore = status;
    },
    SET_PAGINATION(state, payload) {
        if (payload) state.pagination = {...state.pagination, ...payload};
    },
    SET_LIVE_SPORT_SELECTED(state, payload) {
        state.liveSportSelected = payload;
    },
    SET_ACTIVE_SPORT(state, sportId) {
        state.activeSport = sportId ?? null;
    },
    SET_PREVIEW_LIMIT(state, limit) {
        state.previewLimit = (limit != null && limit < DEFAULT_PAGINATION_LIMIT) ? limit : null;
    },
    SET_COUNT_LIVE(state, payload) {
        state.countLive = payload;
    },
    // Vue.set instead of spread: avoids replacing the whole previewLive reference,
    // which would bust getGroupedEvents cache and trigger a full sectionizedGroups recompute.
    SET_HEADER_SIGN(state, headerSign) {
        Vue.set(state.previewLive, 'headerSign', headerSign || []);
    },
    SET_HEADER_OVERTIME(state, headerSignOvertime) {
        Vue.set(state.previewLive, 'headerSignOvertime', headerSignOvertime || null);
    },
    SET_HEADER_PENALTY(state, headerSignPenalty) {
        Vue.set(state.previewLive, 'headerSignPenalty', headerSignPenalty || null);
    },

    SET_PREVIEW_LIVE(state, payload) {
        state.previewLive = payload;
        eventsLookupTable = {};
        oddsLookupTable = {};
        // Wholesale reset — the previous page's frozen uids are no longer
        // meaningful. mapSingleMarketToLookup below repopulates as it walks.
        clearFrozenRegistry();
        finishedEventIds.clear();
        _goalTimers.forEach(timerId => clearTimeout(timerId));
        _goalTimers.clear();
        state.activeGoalEvents = {};

        const eventsData = payload.events || payload;
        if (Array.isArray(eventsData)) {
            eventsData.forEach(group => {
                if (Array.isArray(group)) {
                    group.forEach(event => {
                        if (event.id) eventsLookupTable[event.id] = event;
                        if (Array.isArray(event.odds)) {
                            event.odds.forEach(market => mapSingleMarketToLookup(market, event.id));
                        }
                    });
                }
            });
        }
    },

    APPEND_PREVIEW_LIVE(state, payload) {
        const newGroups = payload.events || [];
        const currentGroups = state.previewLive.events || [];

        newGroups.forEach(newGroup => {
            if (!newGroup || newGroup.length === 0) return;
            const {tournamentId, sportId} = newGroup[0];

            const existingGroupIndex = currentGroups.findIndex(g =>
                g.length > 0 && g[0].tournamentId === tournamentId && g[0].sportId === sportId
            );

            if (existingGroupIndex !== -1) {
                newGroup.forEach(newEvent => {
                    if (!eventsLookupTable[newEvent.id]) {
                        currentGroups[existingGroupIndex].push(newEvent);
                        eventsLookupTable[newEvent.id] = newEvent;
                        if (Array.isArray(newEvent.odds)) {
                            newEvent.odds.forEach(m => mapSingleMarketToLookup(m, newEvent.id));
                        }
                    }
                });
            } else {
                currentGroups.unshift(newGroup);
                newGroup.forEach(newEvent => {
                    if (!eventsLookupTable[newEvent.id]) {
                        eventsLookupTable[newEvent.id] = newEvent;
                        if (Array.isArray(newEvent.odds)) {
                            newEvent.odds.forEach(m => mapSingleMarketToLookup(m, newEvent.id));
                        }
                    }
                });
            }
        });

        state.previewLive.events = [...currentGroups];
    },

    ADD_NEW_EVENT_LIVE(state, eventData) {
        const newEvent = Array.isArray(eventData) ? eventData[0] : eventData;
        if (!newEvent || eventsLookupTable[newEvent.id]) return;

        eventsLookupTable[newEvent.id] = newEvent;
        if (Array.isArray(newEvent.odds)) {
            newEvent.odds.forEach(m => mapSingleMarketToLookup(m, newEvent.id));
        }

        const eventsContainer = state.previewLive.events || state.previewLive;
        if (Array.isArray(eventsContainer)) {
            const group = eventsContainer.find(g =>
                g.length > 0 && g[0].tournamentId === newEvent.tournamentId && g[0].sportId === newEvent.sportId
            );

            if (group) group.push(newEvent);
            else eventsContainer.push([newEvent]);
        }
    },

    // 🛡️ SCUDO MONOTONICO SU SOCKET
    UPDATE_LIVESCORE_FROM_SOCKET(state, payload) {
        const updates = Array.isArray(payload) ? payload : [payload];
        updates.forEach(change => {
            const event = eventsLookupTable[change.id];
            if (event) {

                if (isPayloadStale(event, change)) return;

                if (change.lastUpdate) {
                    Vue.set(event, 'lastUpdate', change.lastUpdate);
                }

                if (change.status != null) event.status = String(change.status);

                if (change.livescore) {
                    const incomingData = Array.isArray(change.livescore) ? change.livescore[0] : change.livescore;

                    // Goal detection: store in dedicated activeGoalEvents state
                    const goalPosition = detectGoalPosition(event, {...change, livescore: incomingData});
                    if (goalPosition !== null) {
                        if (_goalTimers.has(change.id)) clearTimeout(_goalTimers.get(change.id));
                        Vue.set(state.activeGoalEvents, String(change.id), goalPosition);
                        const eventId = String(change.id);
                        _goalTimers.set(eventId, setTimeout(() => {
                            Vue.delete(state.activeGoalEvents, eventId);
                            _goalTimers.delete(eventId);
                        }, 10000));
                    }

                    _patchEventLivescore(event, {livescore: incomingData});

                    if (incomingData.lastUpdate) Vue.set(event.livescore, 'lastUpdate', incomingData.lastUpdate);
                    if (incomingData.livescoreExtraData) event.livescore.livescoreExtraData = incomingData.livescoreExtraData;
                    if (incomingData?.statistics) Vue.set(event.livescore, 'statistics', incomingData.statistics);
                    if (incomingData?.currentIncident && Object.keys(incomingData.currentIncident).length > 0) {
                        Vue.set(event.livescore, 'currentIncident', incomingData.currentIncident);
                    }
                }
            }
        });
    },

    // 🛡️ SCUDO MONOTONICO SU SILENT PATCH
    SILENT_PATCH_PREVIEW_LIVE(state, freshData) {
        const freshGroups = (freshData && Array.isArray(freshData.events)) ? freshData.events : freshData;
        if (!Array.isArray(freshGroups)) return;

        freshGroups.forEach(group => {
            if (!Array.isArray(group)) return;
            group.forEach(fresh => {
                const existing = eventsLookupTable[fresh.id];
                if (!existing) return;

                if (!isPayloadStale(existing, fresh)) {
                    if (fresh.lastUpdate) Vue.set(existing, 'lastUpdate', fresh.lastUpdate);
                    if (fresh.status != null) existing.status = String(fresh.status);

                    _patchEventLivescore(existing, fresh);

                    if (fresh.livescore && fresh.livescore.lastUpdate) {
                        if (existing.livescore) Vue.set(existing.livescore, 'lastUpdate', fresh.livescore.lastUpdate);
                    }
                }

                // Le quote le aggiorno in ogni caso! (Il backend si spera assicuri che le quote non vadano indietro nel tempo)
                _patchEventOdds(existing, fresh.odds, fresh.id);
            });
        });
    },

    SUSPEND_UNSEEN_EVENTS_LIVE_PREVIEW(state, seenIds) {
        for (const eventId in eventsLookupTable) {
            if (seenIds.has(eventId)) continue;
            const event = eventsLookupTable[eventId];
            if (!Array.isArray(event?.odds)) continue;

            event.odds.forEach(market => {
                if (!Array.isArray(market?.outcome)) return;
                market.outcome.forEach(outcome => {
                    outcome.active = false;
                    outcome.status = 0;
                });
            });
        }
    },

    MARK_EVENTS_MATCH_FINISHED(state, eventIds) {
        eventIds.forEach(id => {
            finishedEventIds.add(String(id));
            const event = eventsLookupTable[String(id)];
            if (event) Vue.set(event, 'matchFinished', true);
        });
    },

    INJECT_NEW_EVENTS_FROM_POLL(state, freshGroups) {
        if (!Array.isArray(freshGroups)) return;
        const currentGroups = state.previewLive.events || [];
        const maxEvents = state.previewLimit;
        let hasNew = false;

        freshGroups.forEach(group => {
            if (!Array.isArray(group)) return;
            group.forEach(event => {
                if (!event?.id || eventsLookupTable[String(event.id)]) return;

                if (maxEvents != null) {
                    const currentTotal = currentGroups.reduce((sum, g) => sum + g.length, 0);
                    if (currentTotal >= maxEvents) return;
                }

                hasNew = true;
                Vue.set(event, 'isNewlyAdded', true);
                eventsLookupTable[String(event.id)] = event;

                if (Array.isArray(event.odds)) {
                    event.odds.forEach(m => mapSingleMarketToLookup(m, event.id));
                }

                const groupIdx = currentGroups.findIndex(g =>
                    g.length > 0 && String(g[0].tournamentId) === String(event.tournamentId) && String(g[0].sportId) === String(event.sportId)
                );

                if (groupIdx !== -1) currentGroups[groupIdx].push(event);
                else currentGroups.push([event]);
            });
        });

        if (hasNew) state.previewLive.events = [...currentGroups];
    },

    MARK_EVENT_LEAVING(state, eventId) {
        const event = eventsLookupTable[String(eventId)];
        if (event) Vue.set(event, 'isLeaving', true);
    },

    REMOVE_FINISHED_EVENT(state, eventId) {
        const id = String(eventId);
        if (_goalTimers.has(id)) {
            clearTimeout(_goalTimers.get(id));
            _goalTimers.delete(id);
        }
        Vue.delete(state.activeGoalEvents, id);
        delete eventsLookupTable[id];
        finishedEventIds.delete(id);

        for (const key in oddsLookupTable) {
            if (String(oddsLookupTable[key]?.eventId) === id) {
                delete oddsLookupTable[key];
            }
        }

        const groups = state.previewLive.events || [];
        for (let i = groups.length - 1; i >= 0; i--) {
            const idx = groups[i].findIndex(e => String(e.id) === id);
            if (idx !== -1) {
                groups[i].splice(idx, 1);
                if (groups[i].length === 0) groups.splice(i, 1);
                break;
            }
        }
        state.previewLive.events = [...groups];
    },

    UPDATE_ODDS_FROM_SOCKET(state, payload) {
        if (!Array.isArray(payload)) return;

        // Same dirty-set pattern as detailLive — KEY re-apply runs once per
        // market at the end, regardless of how many outcomes of that market
        // changed in the same socket batch.
        const dirtyMarkets = new Set();

        payload.forEach(eventPayload => {
            if (!eventPayload?.odds?.set) return;

            eventPayload.odds.set.forEach(changeItem => {
                const uniqueId = changeItem.unique_id;
                const attr = changeItem._attributes;
                const lookupRef = oddsLookupTable[uniqueId];

                const inTime = attr?.lastUpdate ? new Date(attr.lastUpdate).getTime() : 0;

                if (lookupRef && attr) {
                    const {odd: targetOdd, market: targetMarket} = lookupRef;

                    // Se la singola quota ha un timestamp più recente di quello in arrivo, ignorala.
                    if (targetOdd.lastUpdate && inTime > 0 && inTime < targetOdd.lastUpdate) {
                        return;
                    }

                    const newValue = parseFloat(attr.odds);
                    const oldValue = parseFloat(targetOdd.outcome_odd_value);

                    if (newValue > oldValue) Vue.set(targetOdd, 'type_change', 'up');
                    else if (newValue < oldValue) Vue.set(targetOdd, 'type_change', 'down');

                    // Socket carries the RAW provider value → update both
                    // outcome_odd_original (baseline) and outcome_odd_value
                    // (display). KEY reapply below overwrites display when
                    // enabled.
                    targetOdd.outcome_odd_original = newValue;
                    targetOdd.outcome_odd_value = newValue;
                    targetOdd.status = parseInt(attr.status);
                    // Freeze-suspend authoritative: an outcome with a
                    // live-suspend rule (frozenApplied === true from the
                    // last full fetch) stays as-is regardless of the
                    // provider socket status. Next silent refresh v2
                    // re-confirms via the DB rule state. See task 13.
                    if (targetOdd.frozenApplied !== true) {
                        targetOdd.active = (targetOdd.status === 1);
                    }
                    // Boost invalidation on socket tick — see detailLive
                    // for the full rationale. Raw provider odd wins over
                    // the previously-served boosted value; the next full
                    // refresh confirms via a fresh resolver pass.
                    if (targetOdd.boostApplied === true) {
                        Vue.set(targetOdd, 'boostApplied', false);
                        Vue.set(targetOdd, 'boostPercent', null);
                        Vue.set(targetOdd, 'oddsPreBoost', null);
                    }
                    if (inTime > 0) targetOdd.lastUpdate = inTime;

                    if (targetMarket && attr.line) targetMarket.specifier = attr.line;
                    // R.1 early bailout — skip markets without KEY rule.
                    if (targetMarket && targetMarket.keyApplied != null) dirtyMarkets.add(targetMarket);
                }
            });
        });

        if (dirtyMarkets.size > 0) reapplyKeyOnMarkets(dirtyMarkets);
    },

    RESET_TYPE_CHANGES() {
        for (const key in oddsLookupTable) {
            const ref = oddsLookupTable[key];
            if (ref?.odd?.type_change != null) {
                Vue.set(ref.odd, 'type_change', null);
            }
        }
    },

    // Removes all events belonging to a specific tournament from the preview.
    // Used when the user deselects one tournament in a multi-tournament selection.
    REMOVE_TOURNAMENT_EVENTS(state, tournamentId) {
        const tid = String(tournamentId);
        const groups = state.previewLive.events || [];

        const removedIds = new Set();
        const newGroups = groups.filter(group => {
            if (!group.length || String(group[0].tournamentId) !== tid) return true;
            group.forEach(e => removedIds.add(String(e.id)));
            return false;
        });

        if (removedIds.size === 0) return;

        removedIds.forEach(id => {
            if (_goalTimers.has(id)) {
                clearTimeout(_goalTimers.get(id));
                _goalTimers.delete(id);
            }
            Vue.delete(state.activeGoalEvents, id);
            finishedEventIds.delete(id);
            delete eventsLookupTable[id];
        });
        for (const key in oddsLookupTable) {
            if (removedIds.has(String(oddsLookupTable[key]?.eventId))) delete oddsLookupTable[key];
        }

        state.previewLive.events = newGroups;
    },

    PATCH_PREVIEW_EVENT_STATISTICS(state, { eventId, statistics }) {
        const event = eventsLookupTable[String(eventId)];
        if (!event) return;
        if (!event.livescore) Vue.set(event, 'livescore', {});
        Vue.set(event.livescore, 'statistics', statistics);
    },
};

const actions = {
    setLoadingLive({commit}, status) {
        commit('SET_LOADING_LIVE', status);
    },

    resetPreviewLive({commit}) {
        commit('SET_PREVIEW_LIVE', {events: [], headerSign: [], headerSignOvertime: null, headerSignPenalty: null});
    },

    setLiveSportSelected({commit}, payload) {
        commit('SET_LIVE_SPORT_SELECTED', payload.data);
    },

    async fetchLiveV3ServerSide({commit, rootGetters, dispatch, state}, payload) {
        const page = payload?.page || 1;

        const limit = _resolveLimit(rootGetters, payload.ishome);

        const silent = payload?.silent || false;
        const sport = payload?.sport || state.activeSport || undefined;
        const tournamentId = payload?.tournamentId ?? undefined;

        if (!silent) {
            if (page === 1) commit('SET_LOADING_LIVE', true);
            else commit('SET_LOADING_MORE', true);
        }

        const lang = rootGetters['settings/getLanguage'];
        const apiUrl = rootGetters['settings/getUrlApi'];
        const token = rootGetters['settings/getToken'];
        const platformId = rootGetters['settings/getPlatformId'];

        try {
            const result = await _fetchLivePreviewPage(apiUrl, token, lang, page, limit, sport, platformId, tournamentId);

            if (!result) {
                if (page === 1) commit('SET_PREVIEW_LIVE', {events: [], headerSign: [], headerSignOvertime: null});
                return;
            }

            const {freshData, sportsAvailable, categoriesAvailable, sportId, pagination} = result;

            await dispatch('marketview/setSportsLiveAvailable', sportsAvailable, {root: true});
            await dispatch('marketview/setCategoryLiveAvailable', categoriesAvailable, {root: true});

            if (pagination) commit('SET_PAGINATION', pagination);

            const derivedSportId =
                (freshData.events?.length > 0 && freshData.events[0]?.length > 0)
                    ? freshData.events[0][0].sportId
                    : (sportId || null);

            const currentSportId = rootGetters['marketview/getActiveSportIdLivePreview'];
            if (derivedSportId != null && String(derivedSportId) !== String(currentSportId ?? '')) {
                await dispatch('marketview/setActiveSportIdLivePreview', derivedSportId, {root: true});
            }

            if (page === 1) {
                const derivedSportId = sport || freshData.events?.[0]?.[0]?.sportId || sportId || null;
                commit('SET_ACTIVE_SPORT', derivedSportId);
                commit('SET_PREVIEW_LIMIT', limit);
                commit('SET_PREVIEW_LIVE', freshData);
            } else {
                commit('APPEND_PREVIEW_LIVE', freshData);
                commit('SET_HEADER_OVERTIME', freshData.headerSignOvertime);
                commit('SET_HEADER_PENALTY', freshData.headerSignPenalty);
            }
        } catch (error) {
            console.error("[previewLive.js] Error in fetchLiveV3ServerSide:", error);
            if (page === 1) commit('SET_PREVIEW_LIVE', {
                events: [],
                headerSign: [],
                headerSignOvertime: null,
                headerSignPenalty: null
            });
        } finally {
            if (!silent) {
                commit('SET_LOADING_LIVE', false);
                commit('SET_LOADING_MORE', false);
            }
        }
    },


    async silentRefreshLivePreview({state, commit, rootGetters, dispatch}, payload) {
        if (state.loading || state.loadingMore) return;

        const ishome = payload?.ishome || false;

        // Guard: if we are polling as the home widget (ishome=true) but the main live section
        // has taken ownership of the store (previewLimit===null means limit>=100 was loaded),
        // bail out immediately. This prevents background home-widget poll timers from
        // interfering with the main live section's data and making unexpected tournament calls.
        if (ishome && state.previewLimit === null) return;
        const {pagination, liveSportSelected, activeSport} = state;
        const sport = activeSport ?? liveSportSelected?.id ?? liveSportSelected?.sportId ?? undefined;
        const lang = rootGetters['settings/getLanguage'];
        const apiUrl = rootGetters['settings/getUrlApi'];
        const token = rootGetters['settings/getToken'];
        const platformId = rootGetters['settings/getPlatformId'];

        const limit = _resolveLimit(rootGetters, ishome);
        // For home widget (ishome) we only ever show page 1, so don't re-fetch beyond it.
        const lastPage = ishome ? 1 : (pagination.page || 1);

        // Mirror the active tournament filter so the silent poll stays consistent
        // with what is currently displayed (avoids injecting events from other tournaments).
        const activeTournamentIds = rootGetters['marketview/getActiveTournamentIdLivePreview'] || [];

        let pageRequests;
        if (activeTournamentIds.length > 1) {
            const STAGGER_MS = 200;
            pageRequests = activeTournamentIds.map((tId, i) =>
                new Promise(resolve => setTimeout(resolve, i * STAGGER_MS)).then(() =>
                    _fetchLivePreviewPage(apiUrl, token, lang, 1, limit, sport, platformId, tId)
                )
            );
        } else {
            const tournamentId = activeTournamentIds.length === 1 ? activeTournamentIds[0] : undefined;
            pageRequests = [];
            for (let page = 1; page <= lastPage; page++) {
                pageRequests.push(_fetchLivePreviewPage(apiUrl, token, lang, page, limit, sport, platformId, tournamentId));
            }
        }

        try {
            const results = await Promise.all(pageRequests);
            const seenEventIds = new Set();
            const allFreshGroups = [];
            let isMetadataUpdated = false;
            let latestTotalPages = state.pagination.totalPages || 1;
            // Merge header signs across all results: keep first non-empty found.
            // If all results return null/empty, reset to null/[].
            let mergedHeaderSign = null;
            let mergedHeaderSignOvertime = null;
            let mergedHeaderSignPenalty = null;

            for (const result of results) {
                if (!result) continue;

                if (!isMetadataUpdated) {
                    if (result.sportsAvailable) dispatch('marketview/setSportsLiveAvailable', result.sportsAvailable, {root: true});
                    if (result.categoriesAvailable) dispatch('marketview/setCategoryLiveAvailable', result.categoriesAvailable, {root: true});
                    isMetadataUpdated = true;
                }

                if (result.pagination?.totalPages != null) {
                    latestTotalPages = result.pagination.totalPages;
                }

                if (!mergedHeaderSign && result.freshData.headerSign?.length > 0) {
                    mergedHeaderSign = result.freshData.headerSign;
                }
                if (!mergedHeaderSignOvertime && result.freshData.headerSignOvertime?.length > 0) {
                    mergedHeaderSignOvertime = result.freshData.headerSignOvertime;
                }
                if (!mergedHeaderSignPenalty && result.freshData.headerSignPenalty?.length > 0) {
                    mergedHeaderSignPenalty = result.freshData.headerSignPenalty;
                }

                commit('SILENT_PATCH_PREVIEW_LIVE', result.freshData);

                const groups = Array.isArray(result.freshData.events) ? result.freshData.events : result.freshData;
                if (Array.isArray(groups)) {
                    groups.forEach(group => {
                        if (Array.isArray(group)) {
                            group.forEach(evt => {
                                if (evt.id != null) seenEventIds.add(String(evt.id));
                            });
                            allFreshGroups.push(group);
                        }
                    });
                }
            }

            if (mergedHeaderSign) commit('SET_HEADER_SIGN', mergedHeaderSign);
            commit('SET_HEADER_OVERTIME', mergedHeaderSignOvertime);
            commit('SET_HEADER_PENALTY', mergedHeaderSignPenalty);

            if (allFreshGroups.length > 0) {
                commit('INJECT_NEW_EVENTS_FROM_POLL', allFreshGroups);
            }

            if (lastPage >= latestTotalPages) {
                commit('SUSPEND_UNSEEN_EVENTS_LIVE_PREVIEW', seenEventIds);
            }

            const unseenEventIds = Object.keys(eventsLookupTable).filter(id =>
                !seenEventIds.has(id) && !finishedEventIds.has(id)
            );

            if (unseenEventIds.length > 0) {
                try {
                    const events = unseenEventIds.map(eventId => ({
                        provider: eventsLookupTable[eventId].provider || 'lsport',
                        eventId
                    }));
                    const checkRes = await axios.post(
                        `${apiUrl}/v3/fixtures/status-check`,
                        {events},
                        {headers: {Authorization: `Bearer ${token}`}}
                    );

                    // 🔴 FIX IBRIDO: Controllo incrociato tra DB e Livescore
                    const finishedIds = (checkRes.data?.data || [])
                        .filter(e => {
                            const eventId = String(e.eventId);
                            const st = parseInt(e.status);

                            const isStatusFinished = st === EVENT_STATUS.FINISHED;

                            const cachedEvent = eventsLookupTable[eventId];
                            const currentPeriod = parseInt(cachedEvent?.livescore?.scoreboard?.currentPeriod || '0');
                            const isPeriodFinished = currentPeriod >= 100; // 100 = Full Time

                            return isStatusFinished || isPeriodFinished;
                        })
                        .map(e => String(e.eventId));

                    if (finishedIds.length > 0) {
                        commit('MARK_EVENTS_MATCH_FINISHED', finishedIds);
                        dispatch('scheduleFinishedEventRemoval', finishedIds);
                    }

                    // For home widget (limit < full): events that stayed on screen but fell out
                    // of the server's top-N are not covered by the main poll response.
                    // Fetch their tournaments individually and patch them in-place so their
                    // scores/odds stay fresh. We patch only — no injection from these results.
                    if (ishome) {
                        const finishedSet = new Set(finishedIds);
                        const stillLiveIds = unseenEventIds.filter(id => !finishedSet.has(id));

                        if (stillLiveIds.length > 0) {
                            const tournamentMap = {};
                            stillLiveIds.forEach(id => {
                                const ev = eventsLookupTable[id];
                                if (!ev) return;
                                const tid = String(ev.tournamentId);
                                if (!tournamentMap[tid]) tournamentMap[tid] = true;
                            });

                            const topUpRequests = Object.keys(tournamentMap).map(tid =>
                                _fetchLivePreviewPage(apiUrl, token, lang, 1, 50, sport, platformId, tid)
                            );
                            const topUpResults = await Promise.allSettled(topUpRequests);
                            topUpResults.forEach(r => {
                                if (r.status === 'fulfilled' && r.value) {
                                    commit('SILENT_PATCH_PREVIEW_LIVE', r.value.freshData);
                                }
                            });
                        }
                    }
                } catch (apiError) {
                    console.error("[previewLive.js] Error checking finished status:", apiError);
                }
            }
        } catch (error) {
            console.error("[previewLive.js] Error during silent refresh:", error);
        }

        _scheduleTypeChangeReset(commit);
    },
    scheduleFinishedEventRemoval({commit, rootGetters}, eventIds) {
        const removalMs = rootGetters['settings/getFinishedEventRemovalMs'];
        const FADE_MS = 800;
        eventIds.forEach(eventId => {
            setTimeout(() => {
                commit('MARK_EVENT_LEAVING', eventId);
                setTimeout(() => {
                    commit('REMOVE_FINISHED_EVENT', eventId);
                }, FADE_MS);
            }, removalMs);
        });
    },

    async fetchAndMergeTournament({commit, rootGetters, state}, {sport, tournamentId}) {
        const lang = rootGetters['settings/getLanguage'];
        const apiUrl = rootGetters['settings/getUrlApi'];
        const token = rootGetters['settings/getToken'];
        const platformId = rootGetters['settings/getPlatformId'];
        const limit = _resolveLimit(rootGetters);
        try {
            const result = await _fetchLivePreviewPage(
                apiUrl, token, lang, 1, limit,
                sport || state.activeSport, platformId, tournamentId
            );
            if (result) {
                commit('APPEND_PREVIEW_LIVE', result.freshData);
                // If the current headerSign is empty (e.g. first loaded tournament was overtime-only)
                // and the appended tournament has regular markets, apply them.
                if (result.freshData.headerSign?.length > 0 && !(state.previewLive.headerSign?.length > 0)) {
                    commit('SET_HEADER_SIGN', result.freshData.headerSign);
                }
                if (result.freshData.headerSignOvertime?.length > 0) {
                    commit('SET_HEADER_OVERTIME', result.freshData.headerSignOvertime);
                }
                if (result.freshData.headerSignPenalty?.length > 0) {
                    commit('SET_HEADER_PENALTY', result.freshData.headerSignPenalty);
                }
            }
        } catch (e) {
            console.error('[previewLive] fetchAndMergeTournament error:', e);
        }
    },

    removeTournamentEvents({commit}, tournamentId) {
        commit('REMOVE_TOURNAMENT_EVENTS', tournamentId);
    },

    socketUpdateLivescore({commit}, payload) {
        commit('UPDATE_LIVESCORE_FROM_SOCKET', payload);
    },

    socketUpdateOdds({commit}, payload) {
        commit('UPDATE_ODDS_FROM_SOCKET', payload);
        _scheduleTypeChangeReset(commit);
    },

    async fetchStatisticsForPreviewEvent({ commit, rootGetters }, { eventId, sportId, tournamentId }) {
        const apiUrl = rootGetters['settings/getUrlApi'];
        const token = rootGetters['settings/getToken'];
        const body = {
            eventId,
            platformId: rootGetters['settings/getPlatformId'],
            type: 'groupOdds',
            section: 'sport',
            typegroup: 'detail',
            sportId: sportId || '',
            tournamentId: tournamentId || '',
            lang: rootGetters['settings/getLanguage'],
            product_id: 1,
            limit: 1
        };
        try {
            const res = await axios.post(
                `${apiUrl}/v3/events/live/event-detail`,
                body,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const stats = res.data?.event?.livescore?.statistics;
            // Commit sempre: anche array vuoto sblocca lo skeleton nel componente
            commit('PATCH_PREVIEW_EVENT_STATISTICS', {
                eventId: String(eventId),
                statistics: Array.isArray(stats) ? stats : []
            });
        } catch (_) {
            // In caso di errore sblocca comunque lo skeleton con array vuoto
            commit('PATCH_PREVIEW_EVENT_STATISTICS', { eventId: String(eventId), statistics: [] });
        }
    }
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};